import React, { useState } from 'react';
import FadeIn from '../shared/FadeIn';
import ScrollRevealText from '../shared/ScrollRevealText';
import FAQAccordion from '../shared/FAQAccordion';
import { getPricingPlans, getSapotNoteAddon } from '../../data/pricingData';
import { getHomeFaqs } from '../../data/faqData';
import { useLang, useT } from '../../i18n/LanguageContext';
import strings from './PricingPage.i18n';
import './PricingPage.css';

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const { lang } = useLang();
  const t = useT(strings);
  const pricingPlans = getPricingPlans(lang);
  const sapotNoteAddon = getSapotNoteAddon(lang);
  const homeFaqs = getHomeFaqs(lang);

  const handleCTA = () => {
    window.open(
      'https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI',
      '_blank'
    );
  };

  return (
    <div className="pricing-page">
      {/* Sections 1-3: Hero + Toggle + Cards (unified gradient) */}
      <section className="pricing-top gradient-hero">
        <div className="pricing-hero">
          <FadeIn>
            <span className="pricing-hero__label">{t.label}</span>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="pricing-hero__title">
              {t.titlePart1}<span className="text-accent">{t.titleAccent}</span>{t.titlePart2}
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <ScrollRevealText className="pricing-hero__subtitle">
              {t.subtitle}
            </ScrollRevealText>
          </FadeIn>
        </div>

        <FadeIn delay={300}>
          <div className="pricing-toggle">
            <button
              className={`pricing-toggle__btn ${!isAnnual ? 'pricing-toggle__btn--active' : ''}`}
              onClick={() => setIsAnnual(false)}
            >
              {t.monthly}
            </button>
            <button
              className={`pricing-toggle__btn ${isAnnual ? 'pricing-toggle__btn--active' : ''}`}
              onClick={() => setIsAnnual(true)}
            >
              {t.annual}
            </button>
          </div>
        </FadeIn>

        <div className="pricing-cards">
          {pricingPlans.map((plan, index) => (
            <FadeIn key={plan.name} delay={index * 120}>
              <div
                className={`pricing-card ${plan.featured ? 'pricing-card--featured' : ''}`}
              >
                {plan.featured && (
                  <span className="pricing-card__badge">{t.mostPopular}</span>
                )}
                <h3 className="pricing-card__name">{plan.name}</h3>
                <p className="pricing-card__description">{plan.description}</p>
                <div
                  className="pricing-card__price-wrapper pricing-card__price-wrapper--fade"
                  key={isAnnual ? 'annual' : 'monthly'}
                >
                  <span className="pricing-card__price">
                    {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="pricing-card__period">
                    {isAnnual ? plan.annualPeriod : plan.monthlyPeriod}
                  </span>
                </div>
                <ul className="pricing-card__features">
                  {plan.features.map((feature, i) => (
                    <li className="pricing-card__feature" key={i}>
                      <span className="pricing-card__feature-check">&#10003;</span>
                      <span className="pricing-card__feature-text">{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.annualOffer && (
                  <div
                    className={`pricing-card__offer ${isAnnual ? 'pricing-card__offer--visible' : ''}`}
                    aria-hidden={!isAnnual}
                  >
                    {plan.annualOffer}
                  </div>
                )}
                <button
                  className={`pricing-card__cta ${plan.featured ? 'pricing-card__cta--featured' : ''}`}
                  onClick={handleCTA}
                >
                  {plan.buttonText}
                </button>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={200}>
          <div className="pricing-addon-wrapper">
            <div className="pricing-addon">
              <div className="pricing-addon__text">
                <span className="pricing-addon__label">{t.addonLabel}</span>
                <h4 className="pricing-addon__name">{sapotNoteAddon.name}</h4>
                <p className="pricing-addon__description">{sapotNoteAddon.description}</p>
              </div>
              <div className="pricing-addon__price-wrapper">
                <span className="pricing-addon__price">{sapotNoteAddon.price}</span>
                <span className="pricing-addon__period">{sapotNoteAddon.period}</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Section 4: FAQ — Image left, accordion right */}
      <section className="pricing-faq">
        <div className="pricing-faq__inner">
          <div className="pricing-faq__grid">
            <div className="pricing-faq__left">
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet={process.env.PUBLIC_URL + '/images/faq%20mobile.png'}
                />
                <img
                  src={process.env.PUBLIC_URL + '/images/faq flow.png'}
                  alt={t.faqImageAlt}
                  className="pricing-faq__image"
                />
              </picture>
            </div>
            <div className="pricing-faq__right">
              <span className="pricing-faq__label">{t.faqLabel}</span>
              <h2 className="pricing-faq__title">{t.faqTitle}</h2>
              <FAQAccordion items={homeFaqs} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
